import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, Menu, X, LayoutDashboard, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import LanguageToggle from "@/components/LanguageToggle";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const linkBase = "text-base font-medium text-zinc-700 hover:text-zinc-900 transition-colors relative py-1";
const linkActive = "text-zinc-900";

function Brand() {
  return (
    <Link to="/" data-testid="brand-link" className="flex items-center gap-3 select-none">
      <img src="/logo.png" alt="Mobile Xpressed" className="h-10 w-auto object-contain" />
      <div className="leading-tight hidden sm:block">
        <div className="font-display font-extrabold text-lg tracking-tight text-brand">Mobile Xpressed</div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 -mt-0.5">Young Bloods Only!</div>
      </div>
    </Link>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const { t } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = (
    <>
      <NavLink data-testid="nav-services" to="/services" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}>{t("nav.repairServices")}</NavLink>
      <NavLink data-testid="nav-batteries" to="/shop?category=Batteries" className={linkBase}>{t("nav.batteries")}</NavLink>
      <NavLink data-testid="nav-parts" to="/shop?category=Parts" className={linkBase}>{t("nav.parts")}</NavLink>
      <NavLink data-testid="nav-accessories" to="/shop?category=Accessories" className={linkBase}>{t("nav.accessories")}</NavLink>
    </>
  );

  return (
    <header className="nav-glass sticky top-0 z-40 border-b border-zinc-200/60">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-6">
        <div className="flex items-center gap-10">
          <Brand />
          <nav className="hidden md:flex items-center gap-8">{nav}</nav>
        </div>

        <div className="flex items-center gap-2">
          <LanguageToggle className="hidden sm:inline-flex" />
          <button
            data-testid="nav-search-button"
            onClick={() => navigate("/shop")}
            className="hidden md:flex w-11 h-11 rounded-full hover:bg-zinc-100 items-center justify-center transition-colors"
            aria-label={t("nav.searchProducts")}
          >
            <Search className="w-[22px] h-[22px] text-zinc-800" />
          </button>
          <button
            data-testid="open-cart-button"
            onClick={() => setOpen(true)}
            className="relative w-11 h-11 rounded-full hover:bg-zinc-100 flex items-center justify-center transition-colors"
            aria-label={t("nav.openCart")}
          >
            <ShoppingBag className="w-[22px] h-[22px] text-zinc-800" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-brand text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="w-11 h-11 rounded-full hover:bg-zinc-100 flex items-center justify-center transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <User className="w-[22px] h-[22px] text-zinc-800" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel className="text-xs">
                  <div className="font-semibold text-zinc-900">{user.name}</div>
                  <div className="text-zinc-500 font-normal">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")}>
                  <User className="w-4 h-4 mr-2" /> {t("nav.myAccount")}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/adminX")}>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {t("nav.admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> {t("nav.logOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button data-testid="nav-login-btn" variant="ghost" className="rounded-full h-11 text-base" onClick={() => navigate("/login")}>{t("nav.signIn")}</Button>
              <Button data-testid="nav-register-btn" className="rounded-full h-11 text-base bg-brand hover:bg-brand-hover text-white" onClick={() => navigate("/register")}>{t("nav.getStarted")}</Button>
            </div>
          )}

          <button className="md:hidden w-11 h-11 rounded-full hover:bg-zinc-100 flex items-center justify-center" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-[22px] h-[22px]" /> : <Menu className="w-[22px] h-[22px]" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-zinc-200/60 bg-white/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3" onClick={() => setMobileOpen(false)}>
            {nav}
            <LanguageToggle className="w-fit" />
            {!user && (
              <div className="flex gap-2 pt-2">
                <Button variant="ghost" className="rounded-full flex-1" onClick={() => navigate("/login")}>{t("nav.signIn")}</Button>
                <Button className="rounded-full flex-1 bg-brand hover:bg-brand-hover text-white" onClick={() => navigate("/register")}>{t("nav.getStarted")}</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
